import React from 'react';
import { Helmet } from 'react-helmet-async';

const DOMAIN = 'https://erwinpaisal.id';
const DEFAULT = {
  title: 'Erwin Paisal — IT Professional Medan | Hardware, Software & Networking',
  description: 'Erwin Paisal adalah IT Professional berpengalaman di Medan, Indonesia. Spesialis perakitan PC, instalasi jaringan LAN/WAN, service laptop, dan pengembangan aplikasi. Hubungi sekarang!',
  keywords: 'IT professional Medan, teknisi komputer Medan, instalasi jaringan Medan, service laptop Medan, IT support Medan, setup router MikroTik Medan, perakitan PC Medan, jasa IT Medan',
  image: `${DOMAIN}/og-image.png`,
};

export default function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  article,
  noindex = false,
}) {
  const t = title ? `${title} | Erwin Paisal` : DEFAULT.title;
  const d = description || DEFAULT.description;
  const k = keywords || DEFAULT.keywords;
  const img = image || DEFAULT.image;
  const u = url ? `${DOMAIN}${url}` : DOMAIN;

  // JSON-LD Structured Data
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Erwin Paisal',
    jobTitle: 'IT Professional',
    description: 'IT Professional spesialis hardware, software, dan networking di Medan, Indonesia.',
    url: DOMAIN,
    email: 'erwin@erwinpaisal.id',
    telephone: '+62812-3456-7890',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Medan',
      addressRegion: 'Sumatera Utara',
      addressCountry: 'ID',
    },
    sameAs: [
      'https://linkedin.com/in/erwinpaisal',
      `https://wa.me/6281234567890`,
    ],
    knowsAbout: ['Hardware', 'Software', 'Networking', 'MikroTik', 'VMware', 'Linux', 'Windows'],
  };

  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Erwin Paisal IT Services',
    description: 'Layanan IT profesional di Medan: instalasi jaringan, service komputer, perakitan PC, dan pengembangan aplikasi.',
    url: DOMAIN,
    telephone: '+62812-3456-7890',
    email: 'erwin@erwinpaisal.id',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Medan',
      addressLocality: 'Medan',
      addressRegion: 'Sumatera Utara',
      addressCountry: 'ID',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 3.5952,
      longitude: 98.6722,
    },
    openingHours: 'Mo-Sa 08:00-20:00',
    priceRange: 'Rp 50.000 - Rp 5.000.000',
    areaServed: ['Medan', 'Sumatera Utara', 'Indonesia'],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Layanan IT',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Instalasi Jaringan LAN/WAN' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Service & Perbaikan Komputer' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Konfigurasi Router MikroTik' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Perakitan PC Custom' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pengembangan Aplikasi Web' } },
      ],
    },
  };

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.cover_image,
    datePublished: article.created_at,
    author: {
      '@type': 'Person',
      name: 'Erwin Paisal',
      url: DOMAIN,
    },
    publisher: {
      '@type': 'Person',
      name: 'Erwin Paisal',
      url: DOMAIN,
    },
  } : null;

  return (
    <Helmet>
      {/* Basic */}
      <title>{t}</title>
      <meta name='description' content={d} />
      <meta name='keywords' content={k} />
      <meta name='author' content='Erwin Paisal' />
      <link rel='canonical' href={u} />
      {noindex && <meta name='robots' content='noindex,nofollow' />}

      {/* Open Graph */}
      <meta property='og:type' content={type} />
      <meta property='og:url' content={u} />
      <meta property='og:title' content={t} />
      <meta property='og:description' content={d} />
      <meta property='og:image' content={img} />
      <meta property='og:site_name' content='Erwin Paisal' />
      <meta property='og:locale' content='id_ID' />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={t} />
      <meta name='twitter:description' content={d} />
      <meta name='twitter:image' content={img} />

      {/* Geo */}
      <meta name='geo.region' content='ID-SU' />
      <meta name='geo.placename' content='Medan, Sumatera Utara' />
      <meta name='geo.position' content='3.5952;98.6722' />
      <meta name='ICBM' content='3.5952, 98.6722' />

      {/* Language */}
      <meta httpEquiv='Content-Language' content='id' />
      <html lang='id' />

      {/* JSON-LD */}
      <script type='application/ld+json'>{JSON.stringify(personSchema)}</script>
      <script type='application/ld+json'>{JSON.stringify(businessSchema)}</script>
      {articleSchema && <script type='application/ld+json'>{JSON.stringify(articleSchema)}</script>}
    </Helmet>
  );
}
